import { Box, Chip, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, ListItemText, MenuItem, Select, SelectChangeEvent, Checkbox, InputLabel } from '@mui/material';
import UpTransition from '../../../shared/UpTransition/UpTransition';
import { useState } from 'react';
import ErrorTypography from '../../../shared/ErrorTypography/ErrorTypography';
import { Trans, useTranslation } from 'react-i18next';
import { Graph as GraphType } from '../../../store/data/types';
import { SelectionT } from 'react-digraph';

type Props = {
  open:boolean;
  graphInfo: GraphType | null;
  selected: SelectionT | null;
  onClose: (success: boolean, name: string, ServicesFrom: string, ServicesTo: string) => void;
};

const EditEdgeDialog = ({ open, selected, graphInfo, onClose }: Props): JSX.Element => {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  
  const [error, setError] = useState(false);
  const [existenceError, setExistenceError] = useState(false);
  const [serviceFrom, setServiceFrom] = useState('');
  const [serviceTo, setServiceTo] = useState('');
  const [serviceToID, setServiceToID] = useState('');
  const [serviceFromID, setServiceFromID] = useState('');
  const graphNodes = graphInfo?.nodes;
  const graphEdges = graphInfo?.edges;
  
  const edge = selected?.edges?.values().next().value;

  const nodesInfo = [graphNodes?.map(node => node.title), graphNodes?.map(node => node.id)]; // 2 rows: First with names of services, Second with IDs
  const nodesNames = graphNodes?.map(node => node.title);
  const nodesIDs = graphNodes?.map(node => node.id);

  let SourceName = '';
  let SourceID = '';
  const idxSource = nodesInfo[1]?.findIndex((element) => (element===edge?.source));
  if (nodesNames && nodesIDs && (idxSource || idxSource===0)) {
    SourceName = String(nodesNames[idxSource]);
    SourceID = String(nodesIDs[idxSource]);
  }

  let TargetName = '';
  let TargetID = '';
  const idxTarget = nodesInfo[1]?.findIndex((element) => (element===edge?.target));
  if (nodesNames && nodesIDs && (idxTarget || idxTarget===0)) {
    TargetName = String(nodesNames[idxTarget]);
    TargetID = String(nodesIDs[idxTarget]);
  }

  const reset = () => {
    setName('');
    setError(false);
    setExistenceError(false);
    setServiceFrom('');
    setServiceTo('');
    setServiceFromID('');
    setServiceToID('');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
//  useEffect(reset, [service]);

  const handleClose = (success: boolean) => () => {
    setExistenceError(false);
    setError(false);
    let validationResult = '';
    if (name === '') {
      validationResult = edge.handleText ; // catch all errors!
    } else {
      validationResult = name;
    }

    if ((name === '') && ((serviceFromID === '') || (serviceFromID === SourceID)) && ((serviceToID === '') || (serviceToID === TargetID))) {
      success = false;
    }

    if (((serviceFromID === serviceToID) && (serviceFromID !== '')) || (serviceFromID === TargetID) || (SourceID === serviceToID) || (SourceID === TargetID)) {
      setError(true);
      return;
    }

    if (success) {
      if (!validationResult) {
        setError(true);
        return;
      }
    }

    if (serviceToID === '') {
        if (serviceFromID === '') {
          // nothing was changed, so we don't need to check for existance of such edge
          onClose(success, validationResult, SourceID, TargetID);
        } else {
          // source was changed. We need to check, if such connection already exists
          const ExistingEdges = graphEdges?.filter(edge => ((edge.handleText !== validationResult) && ((edge.source + '_' + edge.target) === (serviceFromID + '_' + TargetID)) || ((edge.source + '_' + edge.target) === (TargetID + '_' + serviceFromID))));
          if (ExistingEdges && ExistingEdges.length >= 1) {
            setExistenceError(true);
            return;
          }
          onClose(success, validationResult, serviceFromID, TargetID);
        }
    } else if (serviceFromID === '') {
      const ExistingEdges = graphEdges?.filter(edge => ((edge.handleText !== validationResult) && ((edge.source + '_' + edge.target) === (SourceID + '_' + serviceToID)) || ((edge.source + '_' + edge.target) === (serviceToID + '_' + SourceID))));
      if (ExistingEdges && ExistingEdges.length >= 1) {
        setExistenceError(true);
        return;
      }
      onClose(success, validationResult, SourceID, serviceToID);
    } else {
      const ExistingEdges = graphEdges?.filter(edge => ((edge.handleText !== validationResult) && ((edge.source + '_' + edge.target) === (serviceFromID + '_' + serviceToID)) || ((edge.source + '_' + edge.target) === (serviceToID + '_' + serviceFromID))));
      if (ExistingEdges && ExistingEdges.length >= 1) {
        setExistenceError(true);
        return;
      }
      onClose(success, validationResult, serviceFromID, serviceToID);
    }
    reset();
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const handleChangeFrom = (event: SelectChangeEvent<typeof serviceFrom>) => {
    const {
      target: { value },
    } = event;
    let IDsPartRaw = '';
    const idx = nodesNames?.findIndex((element) => (element===value)); // index of each chosen variable in list of nodes
    if (nodesIDs && idx || nodesIDs && idx===0) {
      IDsPartRaw = String(nodesIDs[idx]);
    }
    setServiceFrom(value);
    setServiceFromID(IDsPartRaw); 
  };

  const handleChangeTo = (event: SelectChangeEvent<typeof serviceTo>) => {
    const {
      target: { value },
    } = event;
    let IDsPartRaw = '';
    const idx = nodesNames?.findIndex((element) => (element===value)); // index of each chosen variable in list of nodes
    if (nodesIDs && idx || nodesIDs && idx===0) {
      IDsPartRaw = String(nodesIDs[idx]);
    }
    setServiceTo(value);
    setServiceToID(IDsPartRaw);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose(false)}
      aria-labelledby={'edit-edge-dialog-title'}
      aria-describedby={'edit-edge-dialog-description'}
      fullWidth={true}
      maxWidth={'md'}
      TransitionComponent={UpTransition}
    >
      {(
        <>
          <DialogTitle id={'edit-edge-dialog-title'}>
            Edge von <i><b>"{SourceName}"</b></i> bis <i><b>"{TargetName}"</b></i> ändern
          </DialogTitle>
          <DialogContent>
            <InputLabel id="service-from-select-label">From Service {SourceName}</InputLabel>
            <InputLabel id="service-to-select-label">To Service</InputLabel>
            <Select
              sx={{ m: 1, minWidth: 120 }}
              labelId="service-to-select-label"
              id="service-to-select"
              
              value={serviceTo}
              label="To Service"
              onChange={value => handleChangeTo(value)}
              autoWidth
              displayEmpty
              renderValue={(selected) => {
                if (selected.length===0) {
                  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          <Chip key={TargetName} label={TargetName} />
                        </Box>;
                }
                return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip key={selected} label={selected} />
                      </Box>
              }}
              MenuProps={MenuProps}
            >
              {nodesNames?.map((name) => (
                <MenuItem key={name} value={name}>
                  <Checkbox checked={serviceTo.indexOf(name) > -1} />
                  <ListItemText primary={name} />
                </MenuItem>
              ))}
            </Select>
            {error && <ErrorTypography message={'Bitte Angaben überprüfen'} />}
            {existenceError && <ErrorTypography message={'Diese zwei Knoten sind schon verbindet'} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleClose(true)}>
              <Trans>Apply changes</Trans>
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default EditEdgeDialog;